import type { MissingReportOptions, RanRouteCountOptions, RoutineStepOptions } from '../types/RoutineTimeline.types';
import { hasRunEnded } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/utils/taskStatus';

/**
 * The 1-based route of the routine the robot is on. It ends one above the route count on a run that has ended,
 * and is kept within the routine otherwise. A route marked DONE while more routes follow does not end the run.
 */
export const getRoutineStep = ({
  step,
  taskStatus,
  totalRoutes,
  taskName,
  lastRouteName,
  isRobotCleaning,
}: RoutineStepOptions): number => {
  const hasEnded = hasRunEnded(taskStatus, step, totalRoutes, taskName, lastRouteName, isRobotCleaning);

  return hasEnded ? totalRoutes + 1 : Math.min(Math.max(step ?? 1, 1), totalRoutes + 1);
};

/** How many routes of the run are finished, so how many reports it should have by now. */
export const getFinishedRouteCount = (options: RoutineStepOptions): number =>
  Math.min(getRoutineStep(options) - 1, options.totalRoutes);

/** How many routes of a run that was stopped early actually ran: up to the stopped route or the last reported one. */
export const getRanRouteCount = ({ reports, stoppedStep }: RanRouteCountOptions): number =>
  Math.max(stoppedStep ?? 0, 0, ...reports.map(report => report.routeIndex));

/** The finished routes (1-based) that have no report yet and are still worth waiting for. */
export const getMissingReportRouteIndexes = ({
  reports,
  finishedCount,
  unavailableRouteIndexes,
}: MissingReportOptions): number[] =>
  Array.from({ length: finishedCount }, (_, index) => index + 1).filter(
    routeIndex =>
      !unavailableRouteIndexes.includes(routeIndex) && !reports.some(report => report.routeIndex === routeIndex)
  );
